import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Circle, ArrowRight } from "lucide-react";
import { useNavigate } from "react-router-dom";

interface ProfileData {
  full_name: string | null;
  organisation_name: string | null;
  phone: string | null;
  location_city: string | null;
  bio: string | null;
  company_size: string | null;
  industry: string | null;
}

interface CompletionItem {
  key: string;
  label: string;
  completed: boolean;
  action?: string;
}

export default function EmployerProfileCompletion() {
  const navigate = useNavigate();
  const [completion, setCompletion] = useState(0);
  const [items, setItems] = useState<CompletionItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkProfileCompletion();
  }, []);

  const checkProfileCompletion = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data: profile } = await supabase
        .from("profiles")
        .select("full_name, organisation_name, phone, location_city, bio, company_size, industry")
        .eq("id", user.id)
        .single();

      if (!profile) return;

      const completionItems: CompletionItem[] = [
        {
          key: "organisation",
          label: "Add organisation name",
          completed: !!profile.organisation_name,
          action: "/edit-profile",
        },
        {
          key: "contact",
          label: "Add contact information",
          completed: !!profile.phone,
          action: "/edit-profile",
        },
        {
          key: "location",
          label: "Add organisation location",
          completed: !!profile.location_city,
          action: "/edit-profile",
        },
        {
          key: "bio",
          label: "Write about your organisation",
          completed: !!profile.bio && profile.bio.length > 50,
          action: "/edit-profile",
        },
        {
          key: "size",
          label: "Specify company size",
          completed: !!profile.company_size,
          action: "/edit-profile",
        },
        {
          key: "industry",
          label: "Add industry sector",
          completed: !!profile.industry,
          action: "/edit-profile",
        },
      ];

      setItems(completionItems);

      const completedCount = completionItems.filter(item => item.completed).length;
      const completionPercentage = Math.round((completedCount / completionItems.length) * 100);
      setCompletion(completionPercentage);
    } catch (error) {
      console.error("Error checking profile completion:", error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return null;
  }

  // Don't show if profile is 100% complete
  if (completion === 100) {
    return null;
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Complete Your Organisation Profile</CardTitle>
        <CardDescription>
          A complete profile helps candidates learn more about your organisation
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <div className="flex items-center justify-between text-sm">
            <span className="font-medium">{completion}% Complete</span>
            <span className="text-muted-foreground">
              {items.filter(i => i.completed).length} of {items.length} completed
            </span>
          </div>
          <Progress value={completion} className="h-2" />
        </div>

        <div className="space-y-2">
          {items.slice(0, 3).map((item) => (
            <div
              key={item.key}
              className="flex items-center gap-2 text-sm"
            >
              {item.completed ? (
                <CheckCircle2 className="h-4 w-4 text-green-600 flex-shrink-0" />
              ) : (
                <Circle className="h-4 w-4 text-muted-foreground flex-shrink-0" />
              )}
              <span className={item.completed ? "text-muted-foreground line-through" : ""}>
                {item.label}
              </span>
            </div>
          ))}
          {items.length > 3 && (
            <p className="text-xs text-muted-foreground pl-6">
              +{items.length - 3} more items to complete
            </p>
          )}
        </div>

        <Button
          onClick={() => navigate("/edit-profile")}
          variant="outline"
          className="w-full"
          size="sm"
        >
          Complete Profile
          <ArrowRight className="h-4 w-4 ml-2" />
        </Button>
      </CardContent>
    </Card>
  );
}
